import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  LoadingCard,
  PolicyStatus,
} from '@folio/components';
import { claimInsuredName } from '@folio/components/module-elements/ClaimStatusBadge';
import { ErrorMessage } from '@folio/components/module-elements/ErrorMessage';
import { buttonVariants } from '@folio/components/ui/button';
import { Group, Stack } from '@folio/components/ui/flex';
import { useAssertedParams } from '@folio/lib';
import { formatDate } from '@folio/lib/date-formatting';
import { useQuery } from '@tanstack/react-query';
import {
  type ClaimManagementStateDto,
  claimManagementControllerGetQueryOptions,
  policyControllerGetPolicyQueryOptions,
} from 'korint-sdk';
import { FileTextIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { claimFieldItems } from '../ClaimInformationModule/formatClaimFieldValue';
import { type InformationItem, InformationList } from '../ClaimInformationModule/InformationList';
import { ClaimInternalNotesModule } from '../InternalNotesModule/InternalNotesModule';

const INCIDENT_SECTION = 'incident';

function PolicyCard({ claim }: { claim: ClaimManagementStateDto }) {
  const { t } = useTranslation();
  // Absent when the caller can't read policies: the card then stays hidden
  const { data: policy } = useQuery({
    ...policyControllerGetPolicyQueryOptions(claim.policyId),
    retry: false,
  });

  if (!policy) {
    return null;
  }

  const rows: { id: string; label: string; value: ReactNode }[] = [
    { id: 'policyNumber', label: t('claimOverview.policyNumber'), value: policy.externalId },
    {
      id: 'product',
      label: t('claimOverview.product'),
      value: t(`products.${policy.productId}.label`),
    },
    { id: 'insured', label: t('claimOverview.insured'), value: claimInsuredName(claim) || null },
    {
      id: 'policyStatus',
      label: t('claimOverview.policyStatus'),
      value: <PolicyStatus policy={policy} />,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('claimOverview.policy')}</CardTitle>
      </CardHeader>
      <CardContent>
        <Stack gap={4}>
          <dl className="flex flex-col gap-3 text-sm">
            {rows.map(({ id, label, value }) => (
              <Group key={id} justify="between" gap={4}>
                <dt className="text-muted-foreground">{label}</dt>
                <dd className="text-right font-medium">{value ?? '—'}</dd>
              </Group>
            ))}
          </dl>
          <Link to={`/policies/${policy.id}`} className={buttonVariants({ variant: 'outline' })}>
            <FileTextIcon className="mr-2 size-4" />
            {t('claimDetails.seePolicy')}
          </Link>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function ClaimOverviewModule() {
  const { t, i18n } = useTranslation();
  const { claimId } = useAssertedParams<{ claimId: string }>();
  const {
    data: claim,
    isLoading,
    isError,
  } = useQuery(claimManagementControllerGetQueryOptions(claimId));

  if (isLoading) {
    return <LoadingCard />;
  }

  if (isError || !claim) {
    return <ErrorMessage>{t('claimInformation.error')}</ErrorMessage>;
  }

  const toDate = (iso?: string) => (iso ? formatDate(new Date(iso), i18n.language) : null);
  const amountItems = claimFieldItems(
    claim.fields.filter(({ section, type }) => section === INCIDENT_SECTION && type === 'price'),
    { locale: i18n.language, booleanLabels: { yes: t('options.true'), no: t('options.false') } },
  );

  const summaryItems: InformationItem[] = [
    {
      id: 'occurredAt',
      label: t('claimInformation.occurredAt'),
      value: toDate(claim.incident.occurredAt),
    },
    { id: 'declaredAt', label: t('claimInformation.declaredAt'), value: toDate(claim.submittedAt) },
    ...amountItems,
    { id: 'location', label: t('claimInformation.location'), value: claim.incident.location },
    {
      id: 'description',
      label: t('claimInformation.description'),
      value: claim.incident.description,
      fullWidth: true,
    },
  ];

  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
      <Stack gap={4} className="lg:col-span-2">
        <ClaimInternalNotesModule />
        <Card>
          <CardHeader>
            <Group justify="between" gap={2}>
              <CardTitle>{t('claimOverview.summary')}</CardTitle>
              <Link
                to={`/claims/${claimId}/information`}
                className="text-sm font-medium text-primary hover:underline"
              >
                {t('claimOverview.seeDetails')}
              </Link>
            </Group>
          </CardHeader>
          <CardContent>
            <InformationList items={summaryItems} />
          </CardContent>
        </Card>
      </Stack>
      <PolicyCard claim={claim} />
    </div>
  );
}
